import { loadingGif } from "commonAssets/js/helpers";
import { loading, appDocketAPI, appClaimForm } from "commonAssets/js/constants";
import {
    Footer,
    Header,
    ImportantDates,
    Navigation,
    Page,
    ErrorHandler,
} from "commonAssets/js/classes";
import { Cookies } from "commonAssets/cookies";
import { CustomOptOut } from "./scripts/optOut";
import { siteTitle, makeStandardContactPage } from "commonAssets/js/helpers";
import { I18n } from "commonAssets/i18n";
import languages from "./languages";
import {
    siteInfoLang,
    userLanguage,
    setSiteInfo,
    setSiteInfoLang,
    setEmailInform,
    setPhoneAdmin,
    setEmailConfirm,
    setUserLanguage,
    setAppLocale,
    setI18n,
} from "../../common/assets/js/constants";
import { customInit, customProcess, customMember } from "./scripts/custom";
import { CustomSubmitClaim } from "./scripts/submitClaim";
/**
 * Internationalization (i18n) Handling
 *
 * Sets the langauges available for the distribution.
 *
 * Examples:
 * i18n.languages returns languages in an array
 * i18n.userLanguage returns current languages
 * i18n.getTranslation('string') returns predefined string
 */
const i18n = I18n.create(["en"], {}, languages);
setI18n(() => i18n);

/**
 * Cookies
 * @returns {void}
 */
const setCookies = new Cookies();
setCookies.init();

/**
 * Set the user's language using the value from `setCookies.language()`.
 * @param {Function} setCookiesLanguage - The function that returns the user's language.
 */
setUserLanguage(() => setCookies.language());

/**
 * Set the application locale using the value from `setCookies.locale()`.
 * @param {Function} setCookiesLocale - The function that returns the application locale.
 */
setAppLocale(() => setCookies.locale());

/**
 * Loading GIF
 */
loadingGif(loading, "show");

/**
 * Initialize the analytics by loading the Google Tag Manager script and
 * configuring the dataLayer and gtag.
 */
export function analytics() {
    // Add additional `gtag`s here.
    $.getScript(
        "https://www.googletagmanager.com/gtag/js?id=G-LGF2TN8VN9",
        () => {
            window.dataLayer = window.dataLayer || [];
            function gtag() {
                dataLayer.push(arguments);
            }
            window.gtag_report_conversion = function (url) {
                const callback = function () {
                    if (typeof url != "undefined") {
                        window.location = url;
                    }
                };
                gtag("event", "conversion", {
                    send_to: "AW-10839088199/-X6MCN7agYsbEMe4vbAo",
                    event_callback: callback,
                });
                return false;
            };
            gtag("js", new Date());

            gtag("config", "G-WKXC7VDFFT"); // swatjs
            gtag("config", "G-LGF2TN8VN9"); // general
            gtag("config", "AW-10839088199"); // custom client analytics
            gtag("config", "G-3DLNZZDCX0"); // custom client analytics

            // Angeion analytics tag
            gtag("config", "G-SYQ185QXNH");

            // Client analytics page load
            gtag("event", "conversion", {
                send_to: "AW-10839088199/S-HVCOTLw5ADEMe4vbAo",
            });
        }
    );
    // Add custom pixels/analytics code here.
}

/**
 * Calls the appropriate function to display page content based on the given domainSlug.
 * If domainSlug is empty, the 'home' case will be executed.
 *
 * @param {Array} sitePages - An array containing the website sitePages' information.
 * @param {string} domainSlug - The unique identifier (slug) of the page to be displayed.
 * @param {} importantDates -
 *
 * @example
 * // Assuming sitePages array and domainSlug are defined and valid
 * getPage(sitePages, domainSlug, importantDates);
 */
export function getPage(sitePages, domainSlug, importantDates) {
    loadingGif(loading, "show");
    // Set domainSlug to 'home' if empty
    domainSlug === "" ? siteTitle(sitePages) : siteTitle(sitePages, domainSlug);

    // Misc handling.
    domainSlug = domainSlug || "home";
    const page = new Page(sitePages, domainSlug, importantDates);

    switch (domainSlug) {
        case "home":
            page.home();
            analytics();
            break;

        case "submit-claim":
            const submitClaim = new CustomSubmitClaim(
                appClaimForm,
                userLanguage,
                customInit,
                customProcess,
                customMember
            );
            submitClaim.form();
            analytics();
            break;

        case "opt-out":
            const optOut = new CustomOptOut(
                appClaimForm,
                userLanguage,
                i18n,
                false,
                false
            );
            optOut.form();
            analytics();
            break;

        case "important-documents":
            page.important_documents();
            analytics();
            break;

        case "faqs":
            page.faqs();
            analytics();
            break;

        case "contact-us":
            page.contact(makeStandardContactPage, userLanguage);
            // page.contact();
            analytics();
            break;

        default:
            page.error();
            analytics();
    }
}

/**
 * Fetches domain API data and updates the website with the retrieved data.
 * Updates site title, site name, case number, court name, and builds navigation
 * based on the fetched data. Also adds event listener for hash changes to
 * update page content accordingly.
 *
 * @example
 * // Assuming that appDocketAPI and appLang are defined and valid
 * fetch(appDocketAPI)
 * .then(response => response.json())
 * .then(data => { ... })
 * .catch(error => console.error(error));
 */

fetch(appDocketAPI)
    .then((response) => {
        if (response.status > 299) {
            // Hide loading gif
            loadingGif(loading, "hide", 1000);
            // Display error message
            const error = new ErrorHandler();
            error.display(response);
        }
        return response.json();
    })
    .then((data) => {
        setSiteInfoLang(() => data.findResult.lang[userLanguage]);
        setSiteInfo(() => data.findResult);
        setEmailInform(() => data.findResult.email_info);
        setPhoneAdmin(() => data.findResult.phone);
        setEmailConfirm(() => data.findResult.email_conf);

        document.title = siteInfoLang.SiteTitle;

        // Returns the first value after FQDN. Example: fqdn.com/THIS/foo/bar
        const domainSlug = window.location.pathname.split("/")[1];

        const sitePages = reorderPages(siteInfoLang);

        const header = new Header(
            siteInfoLang.SiteName,
            siteInfoLang.CaseNumber,
            siteInfoLang.CourtName
        );
        const navigation = new Navigation(sitePages, domainSlug, getPage);
        const footer = new Footer();
        const importantDates = new ImportantDates(data);

        const headerElem = document.querySelector("header");
        headerElem.classList.add("py-3");
        headerElem.appendChild(header.createHeader());

        navigation.pathChange(sitePages, domainSlug, importantDates);

        document
            .querySelector("header")
            .insertAdjacentHTML("afterend", navigation.element.outerHTML);
        document.querySelector("footer").appendChild(footer.element);

        getPage(sitePages, domainSlug, importantDates);
    })
    .catch((error) => console.error(error));

function reorderPages(siteInfoLang) {
    const optOutPage = siteInfoLang.Pages.find(
        (page) => page.PageTitle === "Opt Out"
    );
    const submitClaimPage = siteInfoLang.Pages.find(
        (page) => page.PageTitle === "Submit Claim"
    );
    const submitClaimPageIndex = siteInfoLang.Pages.indexOf(submitClaimPage);
    const optOutPageIndex = siteInfoLang.Pages.indexOf(optOutPage);
    siteInfoLang.Pages.splice(optOutPageIndex, 1);
    return [
        ...siteInfoLang.Pages.slice(0, submitClaimPageIndex),
        {
            ...submitClaimPage,
            PageTitle: "Fix Claim",
        },
        {
            ...optOutPage,
            PageTitle: "Opt-Out",
            PageName: "opt-out",
        },
        ...siteInfoLang.Pages.slice(submitClaimPageIndex + 1),
    ];
}
